import { P2GBox } from '@parcel2go/core-ui';
import { P2GGrid } from '@parcel2go/core-ui';
import { ReactNode, useMemo } from 'react';
import P2GContentDropDown from '../../P2GContentDropDown';
import { AddTag } from '../AddTag';
import MarketplaceActions from './MarketplaceActions';
import { ItemActionType } from './SetMarketplaceAction';
import { EditType } from './EditType';
import ItemIds from './ItemIds';
import ItemTag from './ItemTag';
import { P2GLink } from '@parcel2go/core-ui';
import ItemInstructions from './ItemInstructions';
import ImportedDate from './ImportedDate';
import ProviderName from '../../../lib/helpers/ProviderName';
import CancelLabelsPreManifest from './CancelLabelsPreManifest';
import { ManifestIsEditable } from '../../manifests/ManifestCanBeCompleted';
import { onlyUniqueNonBlank } from '../../../lib/helpers/ArrayHelper';
import { MarketplaceService } from '../../../lib/consts/ItemMetaConstants';
import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { ShopProximity } from './ShopProximity';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import ItemExtrasToggle from './ItemExtrasToggle';
import ItemRefundAndClaimTags from './ItemRefundAndClaimTags';

interface props {
  item: SmartSendItemApiModel;
  removeTag: (tag: string) => void;
  saveTags: (tags: string[]) => void;
  edit?: (type: EditType) => void;
  action?: (type: ItemActionType) => void;
  section: string;
  drop?: ReactNode;
  rules?: ReactNode;
  status?: ReactNode;
}

export default function ItemMarketplaceDetails(props: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const tags = useMemo(() => {
    const output = [...props.item.Tags];
    const marketplaceService = props.item?.Meta[MarketplaceService]?.Value;
    if (marketplaceService) {
      output.unshift(marketplaceService);
    }

    return output.filter(onlyUniqueNonBlank);
  }, [props.item.Tags, props.item.Meta]);

  return (
    <P2GBox
      pt={1}
      pr={2}
      pb={1}
      pl={2}
      sx={(t) => ({
        background: `linear-gradient(to bottom, ${t.palette.grey.A100}, transparent)`,
        borderRadius: t.spacing(0, 0, 2, 2)
      })}
      classNames={'p2g-smartsend-item-marketplace-details'}
    >
      <P2GGrid container alignItems="center">
        <P2GGrid item flexGrow={1}>
          <P2GGrid container spacing={2} alignItems="center">
            <P2GGrid item flexShrink={1}>
              <ImportedDate key={props.item.Id} item={props.item} />
            </P2GGrid>
            <ItemIds
              wrapper={(x) => (
                <P2GGrid item flexShrink={1}>
                  {x}
                </P2GGrid>
              )}
              item={props.item}
            />
            {props.drop && (
              <P2GGrid item flexShrink={1}>
                {props.drop}
              </P2GGrid>
            )}
            {props.status && (
              <P2GGrid item flexShrink={1}>
                {props.status}
              </P2GGrid>
            )}
            {props.rules && (
              <P2GGrid item flexShrink={1}>
                {props.rules}
              </P2GGrid>
            )}
            {props.saveTags && (
              <P2GGrid item flexShrink={1}>
                <P2GContentDropDown
                  control={
                    <P2GLink variant="body2">
                      {props.item.Tags.length == 0 ? (
                        <Trans t={t} i18nKey="ADD_TAGS_LINK" defaults=" Add<br />Tags" />
                      ) : (
                        <Trans t={t} i18nKey="EDIT_TAGS_LINK" defaults=" Edit<br />Tags" />
                      )}
                    </P2GLink>
                  }
                >
                  <div style={{ width: '250px' }}>
                    <AddTag section={props.section} selected={props.item.Tags} save={props.saveTags} />
                  </div>
                </P2GContentDropDown>
              </P2GGrid>
            )}
            {tags.map((t) => (
              <P2GGrid item flexShrink={1} key={t}>
                <ItemTag tag={t} />
              </P2GGrid>
            ))}
            {props.item.BookingDetails?.OrderLineId && (
              <>
                <ItemRefundAndClaimTags
                  orderlineId={props.item.BookingDetails?.OrderLineId}
                  wrapper={(x) => (
                    <P2GGrid item flexShrink={1}>
                      {x}
                    </P2GGrid>
                  )}
                />
                {props.item.BookingDetails?.Service?.Provider && props.item.Meta['ManifestId']?.Value && (
                  <>
                    <P2GGrid item flexShrink={1}>
                      <ItemTag tag={`${ProviderName(props.item.BookingDetails.Service.Provider)}`} color="error" />
                    </P2GGrid>
                    {ManifestIsEditable(props.item.ManifestStatus) && <CancelLabelsPreManifest item={props.item} />}
                  </>
                )}
              </>
            )}
          </P2GGrid>
        </P2GGrid>
        <P2GGrid item flexShrink={1}>
          <div className="p2g-smartsend-items-mp-actions">
            <P2GGrid container spacing={1} alignItems="center" gap={1}>
              <ShopProximity item={props.item} wrapper={(x) => <P2GGrid item>{x}</P2GGrid>} />
              <P2GGrid item>
                <ItemInstructions item={props.item} variant={'body2'} />
              </P2GGrid>
              <MarketplaceActions item={props.item} action={props.action} size="small" />
              <P2GGrid item flexShrink={1}>
                <ItemExtrasToggle item={props.item} edit={props.edit} isMobile={false} />
              </P2GGrid>
            </P2GGrid>
          </div>
        </P2GGrid>
      </P2GGrid>
    </P2GBox>
  );
}
